import { useState, useEffect } from "react";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import {
  Plus,
  Edit,
  Trash2,
  Loader2,
  Users,
  Shield,
  CheckCircle,
  XCircle,
  Mail,
  User,
  KeyRound,
  UserPlus,
  Briefcase
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AddSalesRepDialog from "./AddSalesRepDialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const ROLES = [
  { value: "super_admin", label: "Super Admin", description: "Full system access" },
  { value: "hub_admin", label: "Hub Admin", description: "Regional admin access" },
  { value: "project_manager", label: "Project Manager", description: "Full access to Project Manager Portal — projects, PM schedules, proposals & capital quotes" },
  { value: "sales_rep", label: "Sales Rep", description: "CRM + own assigned hospitals only" },
  { value: "trade_contractor", label: "Trade Contractor", description: "Trade Partner Portal only — upload their own proposal for the CSI divisions assigned to them (electrical, plumbing, etc.) plus licences & certifications" },
  { value: "staff", label: "Staff", description: "Limited staff access" }
];

const PERMISSIONS = [
  { value: "full", label: "Full Access" },
  { value: "view_only", label: "View Only" },
  { value: "maintenance_only", label: "Maintenance Only" },
  { value: "orders_only", label: "Orders Only" }
];

export default function UserManagement() {
  const { token, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showAddRep, setShowAddRep] = useState(false);
  const [salesReps, setSalesReps] = useState([]);
  const [promotePrefill, setPromotePrefill] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    password: "",
    role: "hub_admin",
    permissions: ["full"],
    is_active: true
  });

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchSalesReps();
  }, [token]);

  const fetchSalesReps = async () => {
    try {
      const r = await fetch(`${API}/admin/sales-reps`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) {
        const d = await r.json();
        setSalesReps(d.reps || []);
      }
    } catch (e) { /* non-fatal */ }
  };

  // Match a user → existing sales rep by email (case-insensitive)
  const repForUser = (u) => {
    const e = (u.email || "").toLowerCase();
    return salesReps.find((r) => (r.email || "").toLowerCase() === e);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const url = editingUser ? `${API}/users/${editingUser.id}` : `${API}/users`;
      const method = editingUser ? "PUT" : "POST";
      
      const payload = editingUser 
        ? { 
            name: formData.name, 
            role: formData.role, 
            permissions: formData.permissions,
            is_active: formData.is_active,
            ...(formData.password && { password: formData.password })
          }
        : formData;

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const created = await response.json();
        toast.success(editingUser ? "User updated successfully" : "User created successfully");
        // For new users, offer to send a welcome email via Outlook
        if (!editingUser && created?.id) {
          try {
            const w = await fetch(`${API}/users/${created.id}/welcome-email`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (w.ok) {
              const data = await w.json();
              const url = `mailto:${encodeURIComponent(data.to)}?subject=${encodeURIComponent(data.subject)}&body=${encodeURIComponent(data.body)}`;
              if (window.confirm(`Open Outlook now to send welcome email to ${data.to}?\n\nThe message is pre-filled with login URL, role, and instructions.`)) {
                window.location.href = url;
              }
            }
          } catch (e) { /* non-fatal */ }
        }
        setShowDialog(false);
        setEditingUser(null);
        resetForm();
        fetchUsers();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Operation failed");
      }
    } catch (error) {
      toast.error("Failed to save user");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!confirm("Are you sure you want to delete this user?")) return;
    
    try {
      const response = await fetch(`${API}/users/${userId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success("User deleted");
        fetchUsers();
      }
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const handleSendWelcome = async (user) => {
    try {
      const r = await fetch(`${API}/users/${user.id}/welcome-email`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error("welcome-email endpoint failed");
      const data = await r.json();
      const url = `mailto:${encodeURIComponent(data.to)}?subject=${encodeURIComponent(data.subject)}&body=${encodeURIComponent(data.body)}`;
      window.location.href = url;
      toast.success(`Outlook opened — sending welcome to ${data.to}`);
    } catch (e) {
      toast.error("Failed to build welcome email");
    }
  };

  const handleSendResetLink = async (userEmail) => {
    if (!confirm(`Send password reset email to ${userEmail}?`)) return;
    
    try {
      const response = await fetch(`${API}/password-reset/admin-trigger`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          target_email: userEmail,
          target_user_type: "admin"
        })
      });
      
      if (response.ok) {
        toast.success(`Password reset email sent to ${userEmail}`);
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to send reset email");
      }
    } catch (error) {
      toast.error("Failed to send reset email");
    }
  };

  const resetForm = () => {
    setFormData({
      email: "",
      name: "",
      password: "",
      role: "hub_admin",
      permissions: ["full"],
      is_active: true
    });
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      name: user.name,
      password: "",
      role: user.role,
      permissions: user.permissions || ["full"],
      is_active: user.is_active !== false
    });
    setShowDialog(true);
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">User Management</h2>
          <p className="text-slate-500">Manage admin and staff accounts</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => { setPromotePrefill(null); setShowAddRep(true); }}
            variant="outline"
            className="gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            data-testid="users-add-rep-btn"
          >
            <Briefcase className="w-4 h-4" />
            Add Sales Rep
          </Button>
          <Button onClick={() => { resetForm(); setEditingUser(null); setShowDialog(true); }} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            Add User
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">User</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Role</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Permissions</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {users.map((user) => (
              <tr key={user.id} className="hover:bg-slate-50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <User className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{user.name}</p>
                      <p className="text-sm text-slate-500">{user.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    user.role === 'super_admin' ? 'bg-purple-100 text-purple-700' :
                    user.role === 'hub_admin' ? 'bg-blue-100 text-blue-700' :
                    user.role === 'project_manager' ? 'bg-amber-100 text-amber-700' :
                    user.role === 'trade_contractor' ? 'bg-orange-100 text-orange-700' :
                    'bg-slate-100 text-slate-600'
                  }`}>
                    {ROLES.find(r => r.value === user.role)?.label || user.role}
                  </span>
                  {repForUser(user) && (
                    <span className="ml-1.5 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700">
                      <Briefcase className="w-3 h-3" /> REP
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {(user.permissions || []).slice(0, 2).map((p, pi) => (
                      <span key={`${user.id}-${p}-${pi}`} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">{p}</span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3">
                  {user.is_active !== false ? (
                    <span className="flex items-center gap-1 text-emerald-600 text-sm">
                      <CheckCircle className="w-4 h-4" />
                      Active
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-slate-400 text-sm">
                      <XCircle className="w-4 h-4" />
                      Inactive
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={() => openEditDialog(user)} title="Edit user"
                      data-testid={`user-edit-${user.email}`}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-blue-600 hover:bg-blue-50"
                      onClick={() => handleSendWelcome(user)}
                      title="Send welcome email via Outlook"
                      data-testid={`welcome-${user.email}`}
                    >
                      <Mail className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="text-cyan-600 hover:bg-cyan-50"
                      onClick={() => handleSendResetLink(user.email)}
                      title="Send password reset email"
                      data-testid={`reset-password-${user.email}`}
                    >
                      <KeyRound className="w-4 h-4" />
                    </Button>
                    {!repForUser(user) && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-emerald-600 hover:bg-emerald-50"
                        onClick={() => {
                          setPromotePrefill({ name: user.name, email: user.email, phone: user.phone || "" });
                          setShowAddRep(true);
                        }}
                        title="Promote this user to sales rep"
                        data-testid={`promote-rep-${user.email}`}
                      >
                        <UserPlus className="w-4 h-4" />
                      </Button>
                    )}
                    {user.id !== currentUser?.id && (
                      <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(user.id)} title="Delete user">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {users.length === 0 && (
          <div className="py-8 text-center text-slate-500">No users found</div>
        )}
      </div>

      {/* Add/Edit User Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingUser ? "Edit User" : "Add New User"}</DialogTitle>
            <DialogDescription>
              {editingUser ? "Update user details and permissions" : "Create a new admin or staff account"}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!editingUser && (
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="user@sterilemate.com"
                  required
                />
              </div>
            )}
            
            <div>
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Full name"
                required
              />
            </div>

            <div>
              <Label>{editingUser ? "New Password (leave blank to keep current)" : "Password *"}</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                placeholder="••••••••"
                required={!editingUser}
              />
            </div>

            <div>
              <Label>Role</Label>
              <Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map(role => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Permissions</Label>
              <Select 
                value={formData.permissions.find(p => p !== "cost.view") || "full"} 
                onValueChange={(v) => setFormData({
                  ...formData,
                  permissions: formData.permissions.includes("cost.view") ? [v, "cost.view"] : [v],
                })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PERMISSIONS.map(perm => (
                    <SelectItem key={perm.value} value={perm.value}>
                      {perm.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="rounded mt-0.5"
                  checked={formData.permissions.includes("cost.view")}
                  onChange={(e) => setFormData({
                    ...formData,
                    permissions: e.target.checked
                      ? [...formData.permissions, "cost.view"]
                      : formData.permissions.filter(p => p !== "cost.view"),
                  })}
                  data-testid="user-cost-view-toggle"
                />
                <span>
                  <span className="text-sm font-semibold text-amber-900">
                    May see supplier cost &amp; gross profit
                  </span>
                  <span className="block text-[11px] text-amber-800/80">
                    Unlocks the Gross Profit report and the 🔒 cost figures on orders. Super admins,
                    hub admins and accountants already have it; grant it to a project manager or
                    sales rep only if they need it.
                  </span>
                </span>
              </label>
            </div>

            {editingUser && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  className="rounded"
                />
                <Label className="mb-0">Active account</Label>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingUser ? "Update" : "Create")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Shared "Add / Promote to Sales Rep" dialog — also used in CRM + Hospitals */}
      <AddSalesRepDialog
        open={showAddRep}
        onOpenChange={(o) => {
          setShowAddRep(o);
          if (!o) setPromotePrefill(null);
        }}
        token={token}
        prefill={promotePrefill}
        currentUserRole={currentUser?.role}
        onCreated={() => {
          fetchSalesReps();
        }}
      />
    </div>
  );
}
